"use client";

import { useState } from "react";
import Link from "next/link";
import { industries } from "@/lib/seo-database";

interface AuditGap {
  gap: string;
  severity: "high" | "medium" | "low";
  recommendation: string;
}

interface AuditResponse {
  success: boolean;
  audit: {
    score: number;
    result: "optimized" | "gaps_found" | "no_website";
    gaps: AuditGap[];
    findings_summary: string;
    outreach_angle: string;
    has_ai_agent: boolean;
  };
}

const SEVERITY_COLORS = {
  high: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", badge: "bg-red-500/20 text-red-300" },
  medium: { bg: "bg-yellow-500/10", border: "border-yellow-500/30", text: "text-yellow-400", badge: "bg-yellow-500/20 text-yellow-300" },
  low: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400", badge: "bg-blue-500/20 text-blue-300" },
};

export function WebsiteAuditClient() {
  const [url, setUrl] = useState("");
  const [industry, setIndustry] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<AuditResponse | null>(null);

  async function handleAudit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const res = await fetch("/api/audit-website", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: url.trim(),
          industry: industry || undefined,
          email: email || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Audit failed");
      }

      const data: AuditResponse = await res.json();
      setResult(data);

      setTimeout(() => {
        document.getElementById("audit-results")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function getScoreColor(score: number) {
    if (score >= 75) return "text-green-400";
    if (score >= 50) return "text-yellow-400";
    if (score >= 25) return "text-orange-400";
    return "text-red-400";
  }

  function getScoreLabel(score: number) {
    if (score >= 75) return "Well Optimised";
    if (score >= 50) return "Room for Improvement";
    if (score >= 25) return "Significant Gaps";
    return "Needs Urgent Attention";
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-20">
      <nav className="text-sm text-gray-500 dark:text-gray-400 mb-8" aria-label="Breadcrumb">
        <Link href="/" className="hover:text-white transition-colors">Home</Link>
        <span className="mx-2">/</span>
        <Link href="/tools" className="hover:text-white transition-colors">Tools</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-300">Website Audit</span>
      </nav>

      <h1 className="text-3xl md:text-5xl font-bold text-white font-[family-name:var(--font-outfit)] mb-4">
        Free AI Readiness Audit
      </h1>
      <p className="text-xl text-gray-400 mb-2 leading-relaxed">
        Enter your website URL and we&apos;ll analyse your business for automation opportunities.
        See where AI agents can save you time and money.
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
        Powered by our AI audit engine. Takes about 15 seconds. No sign-up required.
      </p>

      {/* Audit Form */}
      <form onSubmit={handleAudit} className="space-y-4 mb-12">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Enter your website URL (e.g. mycompany.co.uk)"
            required
            disabled={loading}
            className="w-full rounded-lg border border-gray-700 bg-gray-900/50 px-5 py-3.5 text-white placeholder-gray-500 focus:border-[#4361ee] focus:outline-none disabled:opacity-50 sm:flex-1"
          />
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            disabled={loading}
            className="w-full rounded-lg border border-gray-700 bg-gray-900/50 px-5 py-3.5 text-white focus:border-[#4361ee] focus:outline-none disabled:opacity-50 sm:w-56"
          >
            <option value="">Select industry</option>
            {industries.map((ind) => (
              <option key={ind.slug} value={ind.name}>
                {ind.emoji} {ind.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email to receive full report (optional)"
            disabled={loading}
            className="w-full rounded-lg border border-gray-700 bg-gray-900/50 px-5 py-3.5 text-white placeholder-gray-500 focus:border-[#4361ee] focus:outline-none disabled:opacity-50 sm:flex-1"
          />
          <button
            type="submit"
            disabled={loading || !url.trim()}
            className="w-full whitespace-nowrap rounded-lg bg-gradient-to-r from-[#4361ee] to-[#7c3aed] px-8 py-3.5 font-semibold text-white transition-all hover:shadow-lg hover:shadow-purple-500/25 disabled:opacity-70 sm:w-auto"
          >
            {loading ? "Analysing..." : "Run Free Audit"}
          </button>
        </div>
      </form>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-[#4361ee] border-r-transparent" />
          <p className="mt-4 text-gray-400">Scraping your website and running AI analysis...</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">This takes about 15 seconds</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-center mb-8">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div id="audit-results" className="space-y-6">
          {/* Score Card */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-8 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Your AI Readiness Score</p>
            <p className={`text-7xl font-bold font-[family-name:var(--font-outfit)] ${getScoreColor(result.audit.score)}`}>
              {result.audit.score}
              <span className="text-2xl text-gray-500 dark:text-gray-400">/100</span>
            </p>
            <p className={`text-lg font-semibold mt-2 ${getScoreColor(result.audit.score)}`}>
              {getScoreLabel(result.audit.score)}
            </p>
            {result.audit.has_ai_agent && (
              <span className="inline-block mt-3 rounded-full bg-green-500/20 px-4 py-1 text-sm text-green-300">
                AI tools detected on your site
              </span>
            )}
          </div>

          {/* Summary */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6">
            <h2 className="text-xl font-bold text-white font-[family-name:var(--font-outfit)] mb-3">Summary</h2>
            <p className="text-gray-300 leading-relaxed">{result.audit.findings_summary}</p>
          </div>

          {/* Gaps */}
          {result.audit.gaps.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-white font-[family-name:var(--font-outfit)]">
                Automation Opportunities ({result.audit.gaps.length} found)
              </h2>
              {result.audit.gaps.map((gap, i) => {
                const colors = SEVERITY_COLORS[gap.severity];
                return (
                  <div key={i} className={`rounded-xl border ${colors.border} ${colors.bg} p-5`}>
                    <div className="flex items-start gap-3">
                      <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${colors.badge}`}>
                        {gap.severity}
                      </span>
                      <div>
                        <p className={`font-semibold ${colors.text}`}>{gap.gap}</p>
                        <p className="text-gray-400 text-sm mt-1">{gap.recommendation}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* CTA */}
          <div className="rounded-xl border border-[#4361ee]/30 bg-[#4361ee]/10 p-8 text-center">
            <h3 className="text-xl font-bold text-white font-[family-name:var(--font-outfit)] mb-2">
              Want These Gaps Fixed Automatically?
            </h3>
            <p className="text-gray-400 mb-4">
              Our AI agents can address every gap identified above. Start with a free trial — no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                href="/signup"
                className="inline-block rounded-lg bg-gradient-to-r from-[#4361ee] to-[#7c3aed] px-8 py-3 font-semibold text-white transition-all hover:shadow-lg hover:shadow-purple-500/25"
              >
                Start Free Trial
              </Link>
              <Link
                href="/pricing"
                className="inline-block rounded-lg border border-gray-600 px-8 py-3 font-semibold text-gray-300 transition-all hover:border-gray-400 hover:text-white"
              >
                View Pricing
              </Link>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">Review pricing, trial details, and cancellation terms before you upgrade.</p>
          </div>

          {/* How it works */}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6">
            <h2 className="text-lg font-bold text-white font-[family-name:var(--font-outfit)] mb-3">How This Audit Works</h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-400 text-sm">
              <li>We scrape your website&apos;s publicly visible content (nothing private)</li>
              <li>Our AI analyses 5 areas: intake, scheduling, communication, after-hours, and AI adoption</li>
              <li>Each area is scored against modern automation standards</li>
              <li>Gaps are ranked by severity (high = losing revenue, medium = inefficiency, low = nice-to-have)</li>
              <li>Results are instant and free — we believe in transparency</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
